import React, { useRef, useEffect, useCallback, useState } from 'react';
import { ReactComponent as ChatSendIcon } from '../../icons/chat-send-icon.svg';
import { ReactComponent as CloseIcon } from '../../icons/close-modal.svg';
import { ReactComponent as PaperclipIcon } from '../../icons/paperclip.svg';
import type { FileUploadState } from './sidebar/types';
import { FilePreview } from './sidebar/FilePreview';
import { FileUploadDropZone } from './sidebar/FileUploadDropZone';
import './dreamie-prompt-modal.scss';

export type DreamiePromptTab = 'chat' | 'history';

export interface DreamiePromptSuggestion {
  label: string;
  value: string;
}

interface DreamiePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  avatarUrl?: string;
  avatarAlt?: string;
  heading: string;
  suggestions?: DreamiePromptSuggestion[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSubmit: (value: string) => void;
  submitting?: boolean;
  placeholder?: string;
  hideInput?: boolean;
  inputPosition?: 'above' | 'below';
  uploadedFiles?: FileUploadState[];
  onFileUpload?: (fileState: FileUploadState) => void;
  onRemoveFile?: (fileId: string) => void;
  onAttachFiles?: (files: File[]) => void;
  children?: React.ReactNode;
  activeTab?: DreamiePromptTab;
  onTabChange?: (tab: DreamiePromptTab) => void;
  showBreadcrumbs?: boolean;
  onBackToChats?: () => void;
  historyContent?: React.ReactNode;
}

const DreamiePromptModal: React.FC<DreamiePromptModalProps> = ({
  isOpen,
  onClose,
  avatarUrl,
  avatarAlt,
  heading,
  suggestions,
  inputValue,
  onInputChange,
  onSubmit,
  submitting = false,
  placeholder = 'Type your question...',
  hideInput = false,
  inputPosition = 'above',
  uploadedFiles,
  onFileUpload,
  onRemoveFile,
  onAttachFiles,
  children,
  activeTab = 'chat',
  onTabChange,
  showBreadcrumbs = false,
  onBackToChats,
  historyContent,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [showBackWarning, setShowBackWarning] = useState(false);

  const handleBackClick = useCallback(() => {
    setShowBackWarning(true);
  }, []);

  const handleConfirmBack = useCallback(() => {
    setShowBackWarning(false);
    onBackToChats?.();
  }, [onBackToChats]);

  const handleCancelBack = useCallback(() => {
    setShowBackWarning(false);
  }, []);

  // Auto-focus textarea when modal opens or input becomes enabled
  useEffect(() => {
    if (isOpen && !submitting && !hideInput && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, submitting, hideInput]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [inputValue]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Track the on-screen keyboard via the Visual Viewport API and expose
  // its height as --keyboard-inset on the document root. The backdrop's
  // padding-bottom consumes that inset so the centred modal stays above
  // the keyboard on iOS PWAs (where the layout viewport doesn't shrink).
  // On desktop and on Android with interactive-widget=resizes-content,
  // the inset stays at 0 and this is a no-op.
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const root = document.documentElement;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--keyboard-inset', `${inset}px`);
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.style.setProperty('--keyboard-inset', '0px');
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === backdropRef.current) onClose();
    },
    [onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (inputValue.trim() && !submitting) {
          onSubmit(inputValue.trim());
        }
      }
    },
    [inputValue, submitting, onSubmit],
  );

  const handleSuggestionClick = useCallback(
    (suggestion: DreamiePromptSuggestion) => {
      onInputChange(suggestion.value);
      textareaRef.current?.focus();
    },
    [onInputChange],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const inputArea = (
    <div className='dreamie-prompt-input-area'>
      {uploadedFiles && uploadedFiles.length > 0 && onRemoveFile && (
        <FilePreview files={uploadedFiles} onRemoveFile={onRemoveFile} />
      )}
      <div className='dreamie-prompt-input-row'>
        {onAttachFiles && (
          <>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (files && onAttachFiles) {
                  onAttachFiles(Array.from(files));
                }
                e.target.value = '';
              }}
            />
            <button
              type='button'
              className='dreamie-prompt-attach'
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              <PaperclipIcon />
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          className='dreamie-prompt-textarea'
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={submitting}
        />
        <button
          className='dreamie-prompt-send'
          onClick={() =>
            inputValue.trim() && !submitting && onSubmit(inputValue.trim())
          }
          disabled={!inputValue.trim() || submitting}
        >
          <ChatSendIcon />
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className='dreamie-prompt-backdrop'
      ref={backdropRef}
      onClick={handleBackdropClick}
    >
      <FileUploadDropZone
        onFileUpload={onFileUpload || (() => {})}
        disabled={submitting || !onFileUpload}
        className={`dreamie-prompt-modal${hideInput ? ' response-mode' : ''}`}
      >
        {showBreadcrumbs ? (
          /* ── Active conversation header ── */
          <div className='dreamie-prompt-breadcrumb-bar'>
            <button
              className='dreamie-prompt-breadcrumb-back'
              onClick={handleBackClick}
            >
              ← Back to chats
            </button>
            <button className='dreamie-prompt-close' onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        ) : (
          /* ── Initial view ── */
          <>
            {/* Close button (absolute top-right) */}
            <button className='dreamie-prompt-close' onClick={onClose}>
              <CloseIcon />
            </button>

            {/* Avatar */}
            {avatarUrl && (
              <div className='dreamie-prompt-avatar'>
                <img src={avatarUrl} alt={avatarAlt || 'Assistant'} />
              </div>
            )}

            {/* Heading */}
            {heading && <h1 className='dreamie-prompt-heading'>{heading}</h1>}

            {/* Tab switcher */}
            {onTabChange && (
              <div className='dreamie-prompt-tabs'>
                <button
                  className={`dreamie-prompt-tab${activeTab === 'chat' ? ' active' : ''}`}
                  onClick={() => onTabChange('chat')}
                >
                  Chat
                </button>
                <button
                  className={`dreamie-prompt-tab${activeTab === 'history' ? ' active' : ''}`}
                  onClick={() => onTabChange('history')}
                >
                  History
                </button>
              </div>
            )}

            {/* Suggestions */}
            {activeTab === 'chat' && suggestions && suggestions.length > 0 && (
              <div className='dreamie-prompt-suggestions'>
                {suggestions.map((s, i) => (
                  <p
                    key={i}
                    className='dreamie-prompt-suggestion'
                    onClick={() => handleSuggestionClick(s)}
                  >
                    {s.label}
                  </p>
                ))}
              </div>
            )}

            {/* History tab content */}
            {activeTab === 'history' &&
              (historyContent || (
                <div className='dreamie-prompt-history-placeholder'>
                  <p>No chat history yet</p>
                </div>
              ))}
          </>
        )}

        {/* Input area (above children) */}
        {!hideInput && inputPosition === 'above' && inputArea}

        {/* Slot for custom content (response area, buttons, etc.) */}
        {children}

        {/* Input area (below children) */}
        {!hideInput && inputPosition === 'below' && inputArea}

        {/* Back confirmation warning */}
        {showBackWarning && (
          <div
            className='dreamie-back-warning-overlay'
            onClick={handleCancelBack}
          >
            <div
              className='dreamie-back-warning-dialog'
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className='dreamie-back-warning-title'>
                Leave conversation?
              </h2>
              <p className='dreamie-back-warning-message'>
                Going back will start a new conversation. Your messages are
                saved in History.
              </p>
              <div className='dreamie-back-warning-actions'>
                <button
                  type='button'
                  className='dreamie-back-warning-btn dreamie-back-warning-btn--cancel'
                  onClick={handleCancelBack}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='dreamie-back-warning-btn dreamie-back-warning-btn--confirm'
                  onClick={handleConfirmBack}
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}
      </FileUploadDropZone>
    </div>
  );
};

export default DreamiePromptModal;
